import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {from, Observable, of} from 'rxjs';
import {concatMap, last, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {JobStatuses} from '../actions/jobStatus.actions';
import {JobStatusService} from '../services/jobStatus.service';
import {JobStatusConfiguration} from '../models/job.status/jobStatusConfiguration.model';
import {JobStatus} from '../models/job.status/jobStatus.model';
import {SearchJob} from '../models/search/searchJob.model';
import {JobStatusSearchRequest} from '../models/job.status/jobStatusSearch.model';
import ClearState = Auth.ClearState;
import GetConfigurations = JobStatuses.GetConfigurations;
import GetConfiguration = JobStatuses.GetConfiguration;
import CreateConfiguration = JobStatuses.CreateConfiguration;
import UpdateConfiguration = JobStatuses.UpdateConfiguration;
import DeleteConfiguration = JobStatuses.DeleteConfiguration;
import ClearCurrentConfiguration = JobStatuses.ClearCurrentConfiguration;
import GetJobStatus = JobStatuses.GetJobStatus;
import UpdateJobStatus = JobStatuses.UpdateJobStatus;
import AddJobStatusComment = JobStatuses.AddJobStatusComment;
import SaveJobStatus = JobStatuses.SaveJobStatus;
import SearchJobStatuses = JobStatuses.SearchJobStatuses;
import UpdateQuickSearch = JobStatuses.UpdateQuickSearch;

export interface JobStatusStateModel {
  configurations: Array<JobStatusConfiguration>;
  currentConfiguration: JobStatusConfiguration;
  jobStatus: JobStatus;
  searchResults: Array<SearchJob>;
  /** The overview's last search, so navigating away and back keeps what was being looked at. */
  searchRequest: JobStatusSearchRequest;
  quickSearch: string;
}

@State<JobStatusStateModel>({
  name: 'jobStatus',
  defaults: {
    configurations: [],
    currentConfiguration: null,
    jobStatus: null,
    searchResults: [],
    searchRequest: null,
    quickSearch: ''
  }
})
@Injectable()
export class JobStatusState {
  constructor(private jobStatusService: JobStatusService) {
  }

  @Selector()
  static configurations(state: JobStatusStateModel): Array<JobStatusConfiguration> {
    return state.configurations;
  }

  @Selector()
  static currentConfiguration(state: JobStatusStateModel): JobStatusConfiguration {
    return state.currentConfiguration;
  }

  @Selector()
  static jobStatus(state: JobStatusStateModel): JobStatus {
    return state.jobStatus;
  }

  @Selector()
  static searchResults(state: JobStatusStateModel): Array<SearchJob> {
    return state.searchResults;
  }

  @Selector()
  static searchRequest(state: JobStatusStateModel): JobStatusSearchRequest {
    return state.searchRequest;
  }

  @Selector()
  static quickSearch(state: JobStatusStateModel): string {
    return state.quickSearch;
  }

  @Action(GetConfigurations)
  getConfigurations(ctx: StateContext<JobStatusStateModel>, action: GetConfigurations): Observable<JobStatusConfiguration[]> {
    if (!action.force) {
      const configurations = ctx.getState().configurations;
      if (configurations.length > 0) {
        return of(configurations);
      }
    }
    return this.jobStatusService.getConfigurations().pipe(
      tap(configurations => {
        ctx.patchState({
          configurations
        });
      })
    );
  }

  @Action(GetConfiguration)
  getConfiguration(ctx: StateContext<JobStatusStateModel>, action: GetConfiguration): Observable<JobStatusConfiguration> {
    if (!action.force) {
      const configuration = ctx.getState().currentConfiguration;
      if (configuration && configuration.id === action.id) {
        return of(configuration);
      }
    }
    return this.jobStatusService.getConfiguration(action.id).pipe(
      tap(configuration => {
        ctx.patchState({
          currentConfiguration: configuration
        });
      })
    );
  }

  @Action(CreateConfiguration)
  createConfiguration(ctx: StateContext<JobStatusStateModel>, action: CreateConfiguration): Observable<JobStatusConfiguration> {
    return this.jobStatusService.createConfiguration(action.configuration).pipe(
      tap(configuration => {
        ctx.patchState({
          currentConfiguration: configuration,
          configurations: []
        });
      })
    );
  }

  @Action(UpdateConfiguration)
  updateConfiguration(ctx: StateContext<JobStatusStateModel>, action: UpdateConfiguration): Observable<JobStatusConfiguration> {
    return this.jobStatusService.updateConfiguration(action.configuration).pipe(
      tap(configuration => {
        ctx.patchState({
          currentConfiguration: configuration,
          configurations: []
        });
      })
    );
  }

  @Action(DeleteConfiguration)
  deleteConfiguration(ctx: StateContext<JobStatusStateModel>, action: DeleteConfiguration): Observable<JobStatusConfiguration> {
    return this.jobStatusService.deleteConfiguration(action.configuration.id).pipe(
      tap(() => {
        ctx.patchState({
          currentConfiguration: null,
          configurations: ctx.getState().configurations.filter(configuration => configuration.id !== action.configuration.id)
        });
      })
    );
  }

  @Action(ClearCurrentConfiguration)
  clearCurrentConfiguration(ctx: StateContext<JobStatusStateModel>): void {
    ctx.patchState({
      currentConfiguration: null
    });
  }

  @Action(GetJobStatus)
  getJobStatus(ctx: StateContext<JobStatusStateModel>, action: GetJobStatus): Observable<JobStatus> {
    if (!action.force) {
      const jobStatus = ctx.getState().jobStatus;
      if (jobStatus && jobStatus.jobId === action.jobId) {
        return of(jobStatus);
      }
    }
    return this.jobStatusService.getJobStatus(action.jobId).pipe(
      tap(jobStatus => {
        ctx.patchState({
          jobStatus
        });
      })
    );
  }

  @Action(UpdateJobStatus)
  updateJobStatus(ctx: StateContext<JobStatusStateModel>, action: UpdateJobStatus): Observable<JobStatus> {
    return this.jobStatusService.updateJobStatus(action.jobId, action.statusId).pipe(
      tap(jobStatus => {
        ctx.patchState({
          jobStatus
        });
      })
    );
  }

  @Action(AddJobStatusComment)
  addJobStatusComment(ctx: StateContext<JobStatusStateModel>, action: AddJobStatusComment): Observable<JobStatus> {
    return this.jobStatusService.addComment(action.jobId, action.comment).pipe(
      tap(jobStatus => {
        ctx.patchState({
          jobStatus
        });
      })
    );
  }

  @Action(SaveJobStatus)
  saveJobStatus(ctx: StateContext<JobStatusStateModel>, action: SaveJobStatus): Observable<JobStatus> {
    const requests: Array<() => Observable<JobStatus>> = [];

    if (action.statusChanged) {
      requests.push(() => this.jobStatusService.updateJobStatus(action.jobId, action.statusId));
    }
    if (action.comment) {
      requests.push(() => this.jobStatusService.addComment(action.jobId, action.comment));
    }
    if (requests.length === 0) {
      return of(ctx.getState().jobStatus);
    }

    return from(requests).pipe(
      concatMap(request => request()),
      last(),
      tap(jobStatus => {
        ctx.patchState({
          jobStatus
        });
      })
    );
  }

  @Action(SearchJobStatuses)
  searchJobStatuses(ctx: StateContext<JobStatusStateModel>, action: SearchJobStatuses): Observable<SearchJob[]> {
    // Copied: the overview binds its form straight to the request, and the store must not change
    // underneath it.
    ctx.patchState({
      searchRequest: {...action.request, statusIds: [...action.request.statusIds]}
    });
    return this.jobStatusService.search(action.request).pipe(
      tap(searchResults => {
        ctx.patchState({
          searchResults
        });
      })
    );
  }

  @Action(UpdateQuickSearch)
  updateQuickSearch(ctx: StateContext<JobStatusStateModel>, action: UpdateQuickSearch): void {
    ctx.patchState({
      quickSearch: action.quickSearch
    });
  }

  @Action(ClearState)
  clearState(ctx: StateContext<JobStatusStateModel>): void {
    ctx.setState({
      configurations: [],
      currentConfiguration: null,
      jobStatus: null,
      searchResults: [],
      searchRequest: null,
      quickSearch: ''
    });
  }
}
